import { formatToDateOnly } from '@/utils/dates';
import { AppointmentStatus, BirthSex, GenderIdentity } from '@heidi/schema/ml-scribe';

// Connect
import {
  genieConnect,
  isGenieNotConnectedError,
} from '@/integrations/genie/api/connection';
import { genieFindPatientAppointments } from '@/integrations/genie/api/patient';
import type { GetIntegrationsScheduleType } from '@/types';
import type { CreateIntegrationOnboardingSessionResponse } from '@heidi/integrations/connected-apps/contracts';

type GenieConnectionFormData = {
  instance_url: string;
  sender: string;
  password: string;
};

const isGenieConnectionFormData = (
  payload: unknown
): payload is GenieConnectionFormData => {
  if (typeof payload !== 'object' || payload === null) return false;
  const formData = payload as Record<string, unknown>;
  return (
    typeof formData.instance_url === 'string' &&
    typeof formData.sender === 'string' &&
    typeof formData.password === 'string'
  );
};

const getGenieConnectionFormData = (
  integrationOnboardingSession: CreateIntegrationOnboardingSessionResponse
): GenieConnectionFormData => {
  const payload = integrationOnboardingSession.steps.at(0)?.payload?.form_data;
  if (!isGenieConnectionFormData(payload)) {
    throw new Error('Missing Genie connection details from onboarding session');
  }

  return payload;
};

const connectGenieFromOnboardingSession = async (
  integrationOnboardingSession: CreateIntegrationOnboardingSessionResponse
): Promise<string> => {
  const payload = getGenieConnectionFormData(integrationOnboardingSession);
  const connectionId = await genieConnect({
    instanceUrl: payload.instance_url,
    sender: payload.sender,
    password: payload.password,
  });
  if (!connectionId) {
    throw new Error('Failed to reconnect to Genie');
  }

  return connectionId;
};

export const retryGenieOperationAfterReconnect = async <T>({
  integrationOnboardingSession,
  operation,
}: {
  integrationOnboardingSession: CreateIntegrationOnboardingSessionResponse;
  operation: () => Promise<T>;
}): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (!isGenieNotConnectedError(error)) {
      throw error;
    }

    await connectGenieFromOnboardingSession(integrationOnboardingSession);
    return await operation();
  }
};

/**
 * Generate array of dates between start and end (inclusive)
 * @param start - Date string in format 'YYYY-MM-DD'
 * @param end - Date string in format 'YYYY-MM-DD'
 * @returns Array of date strings in format 'YYYY-MM-DD'
 */
const generateDateRange = (start: string, end: string): string[] => {
  const dates: string[] = [];
  const startDate = new Date(start);
  const endDate = new Date(end);

  for (
    let date = new Date(startDate);
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    dates.push(date.toISOString().split('T').at(0) ?? '');
  }

  return dates;
};

export const getAppointmentsForFE = async (
  startDate: string,
  endDate: string,
  practitionerId: number
): Promise<GetIntegrationsScheduleType> => {
  try {
    // Generate all dates in the range
    const dates = generateDateRange(startDate, endDate);

    // Fetch appointments for all dates in parallel
    const appointmentPromises = dates.filter(Boolean).map((date) =>
      genieFindPatientAppointments(date.replaceAll('-', '')).catch((error) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        // Return empty array if no appointments found for this date
        if (errorMessage.includes('No matching appointments found')) {
          return [];
        }
        throw error;
      })
    );

    const appointmentsPerDay = await Promise.all(appointmentPromises);

    // Flatten and map appointments to frontend format
    return appointmentsPerDay
      .flat()
      .filter((appt) => appt.practitionerId === practitionerId)
      .map((appt) => {
        return {
          appointment: {
            provider_details: {
              provider: 'Genie',
              unique_identifier: appt.appointmentId.toString(),
            },
            start: appt.dateTime,
            end: appt.dateTime, // Note: we dont have duration info
            status: AppointmentStatus.booked,
            session: null,
          },
          patient: {
            provider_details: {
              provider: 'Genie',
              unique_identifier: appt.patientId.toString(),
            },
            name: [
              {
                given: [appt.firstName],
                family: appt.lastName,
              },
            ],
            gender: GenderIdentity.UN,
            birthDate: formatToDateOnly(appt.dateOfBirth),
            telecom: null,
            sex: BirthSex.UN,
          },
        };
      });
  } catch (error) {
    console.error('error.getSchedule', error);
    return Promise.reject(Error('unknown error'));
  }
};

export const refreshGenieToken = async ({
  integrationOnboardingSession,
}: {
  integrationOnboardingSession: CreateIntegrationOnboardingSessionResponse;
}): Promise<{ connectionId: string | null }> => {
  let connectionId: string | null = null;
  try {
    connectionId = await connectGenieFromOnboardingSession(integrationOnboardingSession);
  } catch (error) {
    console.error('failed to connect to genie', error);
  }

  return { connectionId };
};

/**
 * Resolves the recipient ID for a letter from the patient's appointment data.
 * Priority: first referrer ID > usual GP ID > undefined.
 *
 * Note: Genie has no single-appointment lookup API. We fetch all appointments for the
 * day via WS_PR_FindPatientsForDay and filter by ID.
 *
 * @param date - Session date in ISO format (e.g. "2026-03-17T13:00:00")
 * @param ehrApptId - The Genie appointment ID from the session's ehr_appt_id
 * @returns The resolved recipient ID, or undefined if no referrer/GP found
 */
export const resolveRecipientId = async (
  date: string,
  ehrApptId: string
): Promise<number | undefined> => {
  try {
    const dateOnly = date.split('T').at(0)?.replaceAll('-', '');
    if (!dateOnly) return undefined;

    const appointments = await genieFindPatientAppointments(dateOnly);
    const appointment = appointments.find(
      (appt) => appt.appointmentId.toString() === ehrApptId
    );
    if (!appointment) return undefined;

    return appointment.referrers.at(0)?.referrerId ?? appointment.usualGp?.gpId;
  } catch (error) {
    console.error('Failed to resolve recipient ID', error);
    return undefined;
  }
};

export const getPractitionerId = (
  integrationOnboardingSession: CreateIntegrationOnboardingSessionResponse
) =>
  Number(
    // @ts-expect-error unknown for now - need to figure out typing later
    integrationOnboardingSession.steps.at(1)?.payload?.form_data?.doctor_id
  );
